/**
 * ============================================================================
 * WALLET STATS COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Wallet statistics display component
 */

import React, { useEffect, useState } from 'react';
import { useWallet } from '../../hooks/useWallet';
import useAuthStore from '../../store/authStore';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Spinner from '../common/Spinner';

/**
 * Wallet Stats Component
 * @returns {JSX.Element}
 */
const WalletStats = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { loadStatistics, statistics, loading } = useWallet();
  const { formatCurrency, language } = useI18n();

  useEffect(() => {
    if (!accessToken) return;
    loadStatistics();
  }, [accessToken, loadStatistics]);

  if (loading && !statistics) {
    return (
      <Card variant="glass" className="p-12">
        <div className="flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  if (!statistics) return null;

  const statCards = [
    {
      label: language === 'fr' ? 'Total crédité' : 'Total Credited',
      value: formatCurrency(statistics.totalCredited || 0, statistics.currency || 'XAF'),
      icon: '💰',
      color: 'from-green-500 to-emerald-500',
    },
    {
      label: language === 'fr' ? 'Total débité' : 'Total Debited',
      value: formatCurrency(statistics.totalDebited || 0, statistics.currency || 'XAF'),
      icon: '💸',
      color: 'from-red-500 to-pink-500',
    },
    {
      label: language === 'fr' ? 'Retraits totaux' : 'Total Withdrawals',
      value: formatCurrency(statistics.totalWithdrawn || 0, statistics.currency || 'XAF'),
      icon: '⬇️',
      color: 'from-purple-500 to-pink-500',
    },
    {
      label: language === 'fr' ? 'Transactions' : 'Transactions',
      value: statistics.totalTransactions || 0,
      icon: '📊',
      color: 'from-blue-500 to-cyan-500',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {statCards.map((stat, index) => (
        <Card
          key={index}
          variant="glass"
          hover
          className="animate-fadeInUp"
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-2xl shadow-lg`}>
              {stat.icon}
            </div>
            <div className="flex-1">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                {stat.label}
              </p>
              <p className="text-xl font-black text-gray-900 dark:text-white">
                {stat.value}
              </p>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default WalletStats;
